import { useEffect, useRef } from 'react';
import * as THREE from 'three';

interface GlobeProps {
  size?: number;
  /** Empty space (px) inside the mask box around the globe canvas. */
  padding?: number;
  /** Override horizontal padding (px) — defaults to `padding`. */
  paddingX?: number;
  /** Override vertical padding (px) — defaults to `padding`. */
  paddingY?: number;
}

// Equirectangular land mask is rasterised from GeoJSON at runtime, then sampled
// in the fragment shader so we can dot-pattern only the land.
const COUNTRIES_URL =
  'https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_50m_admin_0_countries.geojson';

const MASK_W = 2048;
const MASK_H = 1024;

const VERT = `
varying vec3 vWorldNormal;
varying vec3 vViewDir;
varying vec2 vUv;
void main() {
  vUv = uv;
  vec4 worldPos = modelMatrix * vec4(position, 1.0);
  vWorldNormal = normalize(mat3(modelMatrix) * normal);
  vViewDir = normalize(cameraPosition - worldPos.xyz);
  gl_Position = projectionMatrix * viewMatrix * worldPos;
}
`;

const FRAG = `
precision highp float;
uniform sampler2D u_land;
uniform sampler2D u_uk;
uniform vec3 u_oceanColor;
uniform vec3 u_landColor;
uniform vec3 u_dotColor;
uniform vec3 u_atmosphereColor;
uniform float u_dotSize;
uniform float u_dotDensity;
uniform vec3 u_shineCenter;
uniform float u_shineProgress;
uniform float u_shineStrength;
uniform vec3 u_shineColor;
uniform float u_persistentTint;
uniform vec3 u_persistentColor;
varying vec3 vWorldNormal;
varying vec3 vViewDir;
varying vec2 vUv;

void main() {
  // Fresnel rim (front-facing = small, edge = large). Higher exponent makes
  // the rim narrower so the rest of the sphere reads flatter.
  float fres = pow(1.0 - max(0.0, dot(vWorldNormal, vViewDir)), 4.0);

  // Equirectangular UV → 3D point on unit sphere → regular grid in 3D
  // (gridding the UV directly distorts dots at the poles).
  float lat = (vUv.y - 0.5) * 3.14159265;
  float lng = (vUv.x - 0.5) * 6.28318530;
  vec3 p = vec3(cos(lat) * cos(lng), sin(lat), cos(lat) * sin(lng));
  vec3 g = fract(p * u_dotDensity) - 0.5;
  float d = length(g);
  float dotMask = 1.0 - smoothstep(u_dotSize, u_dotSize * 1.5, d);

  // Land mask (softened so coastlines aren't jagged)
  float land = texture2D(u_land, vUv).r;
  land = smoothstep(0.3, 0.7, land);

  // Base sphere colour: ocean → land
  vec3 col = mix(u_oceanColor, u_landColor, land);
  // Stamp dots only on land
  col = mix(col, u_dotColor, dotMask * land);
  // Add atmospheric rim
  col = mix(col, u_atmosphereColor, fres * 0.25);

  // Sample UK mask once (used by both transient wave and persistent overlay).
  float uk = texture2D(u_uk, vUv).r;
  uk = smoothstep(0.3, 0.7, uk);

  // Persistent blue overlay covers the whole UK uniformly — its strength is
  // driven by scroll, so the blue fades in during the zoom-in.
  if (u_persistentTint > 0.001) {
    col = mix(col, u_persistentColor, uk * u_persistentTint);
  }

  // Bright ripple band riding outward from UK centroid — pure visual flash on
  // top of the blue, doesn't gate the persistent tint.
  if (u_shineStrength > 0.001) {
    float angle = acos(clamp(dot(p, u_shineCenter), -1.0, 1.0));
    float ringRadius = u_shineProgress * 0.22;
    float ringWidth = 0.04;
    float band = (angle - ringRadius) / ringWidth;
    float ring = exp(-band * band);
    float pop = 1.0 + dotMask * 2.0;
    col += u_shineColor * ring * u_shineStrength * pop * uk;
  }

  // Opaque sphere, with soft alpha fade at the silhouette
  float a = 1.0 - smoothstep(0.85, 1.0, fres);
  a = max(a, fres * 0.5);  // keep the rim glow visible
  gl_FragColor = vec4(col, a);
}
`;

function lngLatToXY(lng: number, lat: number): [number, number] {
  return [((lng + 180) / 360) * MASK_W, ((90 - lat) / 180) * MASK_H];
}

function rasteriseMask(features: any[], filter?: (f: any) => boolean): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = MASK_W;
  c.height = MASK_H;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, MASK_W, MASK_H);
  ctx.fillStyle = '#fff';

  const drawRing = (ring: number[][]) => {
    if (!ring.length) return;
    ctx.beginPath();
    const [x0, y0] = lngLatToXY(ring[0][0], ring[0][1]);
    ctx.moveTo(x0, y0);
    for (let i = 1; i < ring.length; i++) {
      const [x, y] = lngLatToXY(ring[i][0], ring[i][1]);
      ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  };

  for (const feat of features) {
    if (filter && !filter(feat)) continue;
    const g = feat.geometry;
    if (!g) continue;
    if (g.type === 'Polygon') for (const r of g.coordinates) drawRing(r);
    else if (g.type === 'MultiPolygon')
      for (const poly of g.coordinates) for (const r of poly) drawRing(r);
  }
  return c;
}

function isUK(feat: any): boolean {
  const p = feat?.properties ?? {};
  return (
    p.ISO_A2 === 'GB' ||
    p.ISO_A2_EH === 'GB' ||
    p.ADM0_A3 === 'GBR' ||
    p.ADMIN === 'United Kingdom' ||
    p.NAME === 'United Kingdom'
  );
}

export function GlobeView({ size = 500, padding = 0, paddingX, paddingY }: GlobeProps) {
  const padX = paddingX ?? padding;
  const padY = paddingY ?? padding;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(dpr);
    // Canvas matches the OUTER box so nothing is clipped, but the camera FOV
    // is widened proportionally so the globe stays the same on-screen pixel
    // size — the extra pixels just become empty space around the sphere.
    const canvasW = size + 2 * padX;
    const canvasH = size + 2 * padY;
    renderer.setSize(canvasW, canvasH);
    container.innerHTML = '';
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const FOV_BASE = 35;
    // Vertical FOV scales with height so the globe keeps the same pixel height.
    const fov = FOV_BASE * (canvasH / size);
    const camera = new THREE.PerspectiveCamera(fov, canvasW / canvasH, 0.1, 100);
    // Start far out
    camera.position.set(0, 0, 8);
    camera.lookAt(0, 0, 0);

    const geometry = new THREE.SphereGeometry(1, 96, 48);
    const landTex = new THREE.Texture();
    landTex.minFilter = THREE.LinearFilter;
    landTex.magFilter = THREE.LinearFilter;
    landTex.wrapS = THREE.RepeatWrapping;
    const ukTex = new THREE.Texture();
    ukTex.minFilter = THREE.LinearFilter;
    ukTex.magFilter = THREE.LinearFilter;
    ukTex.wrapS = THREE.RepeatWrapping;

    const material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      uniforms: {
        u_land: { value: landTex },
        u_uk: { value: ukTex },
        u_oceanColor: { value: new THREE.Color('#ffffff') },
        u_landColor: { value: new THREE.Color('#f6f6f6') },
        u_dotColor: { value: new THREE.Color('#d4d4d4') },
        u_atmosphereColor: { value: new THREE.Color('#e0e0e0') },
        u_dotSize: { value: 0.04 },
        u_dotDensity: { value: 110.0 },
        u_shineCenter: {
          value: (() => {
            const latR = (54.5 * Math.PI) / 180;
            const lngR = (-2.5 * Math.PI) / 180;
            return new THREE.Vector3(
              Math.cos(latR) * Math.cos(lngR),
              Math.sin(latR),
              Math.cos(latR) * Math.sin(lngR),
            );
          })(),
        },
        u_shineProgress: { value: 0 },
        u_shineStrength: { value: 0 },
        u_shineColor: { value: new THREE.Color('#67e8f9') },
        u_persistentTint: { value: 0 },
        u_persistentColor: { value: new THREE.Color('#000000') },
      },
    });

    const sphere = new THREE.Mesh(geometry, material);
    // Align the texture so the world +Z axis sits at the prime meridian.
    sphere.rotation.y = -Math.PI / 2;
    scene.add(sphere);

    // Fetch + rasterise both the all-land mask and a UK-only mask.
    fetch(COUNTRIES_URL)
      .then((r) => r.json())
      .then((data) => {
        const feats = data.features || [];
        landTex.image = rasteriseMask(feats);
        landTex.needsUpdate = true;
        ukTex.image = rasteriseMask(feats, isUK);
        ukTex.needsUpdate = true;
      })
      .catch(() => {});

    // Scroll-driven camera path: start far/equator, end zoomed-in on UK.
    const animFrom = { dist: 4.5, lat: 0, lng: -30 };
    const animTo = { dist: 1.8, lat: 54.5, lng: -2.5 };
    // Scroll progress is computed from the container's position in the viewport.
    // 0 → just entered the viewport bottom; 1 → near the viewport top.
    const startVH = 0.55;
    const endVH = 0.1;
    let scrollProgress = 0;

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const placeCamera = (lat: number, lng: number, dist: number) => {
      const latR = (lat * Math.PI) / 180;
      const lngR = (lng * Math.PI) / 180;
      camera.position.set(
        dist * Math.cos(latR) * Math.sin(lngR),
        dist * Math.sin(latR),
        dist * Math.cos(latR) * Math.cos(lngR),
      );
      camera.lookAt(0, 0, 0);
    };

    const pinTarget = container.closest('[data-globe-scroll-target]') as HTMLElement | null;
    // Camera zoom completes at this fraction of the pin's scrollable distance.
    // The remaining pin distance keeps the globe locked on-screen while the
    // shine animation plays out.
    const CAMERA_DONE_AT = 0.6;
    const updateProgress = () => {
      if (pinTarget) {
        const rect = pinTarget.getBoundingClientRect();
        const vh = window.innerHeight;
        const total = Math.max(1, rect.height - vh);
        scrollProgress = Math.min(1, Math.max(0, -rect.top / (total * CAMERA_DONE_AT)));
        return;
      }
      const rect = container.getBoundingClientRect();
      const vh = window.innerHeight;
      const start = vh * startVH;
      const end = vh * endVH;
      scrollProgress = Math.min(1, Math.max(0, (start - rect.top) / (start - end)));
    };

    let scrollRaf = 0;
    const onScroll = () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        updateProgress();
      });
    };

    updateProgress();
    placeCamera(animFrom.lat, animFrom.lng, animFrom.dist);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    let rafId = 0;
    const tick = () => {
      const e = easeOutCubic(scrollProgress);
      const dist = animFrom.dist + (animTo.dist - animFrom.dist) * e;
      const lat = animFrom.lat + (animTo.lat - animFrom.lat) * e;
      const lng = animFrom.lng + (animTo.lng - animFrom.lng) * e;
      placeCamera(lat, lng, dist);

      // UK fades from grey to light blue as scroll progresses.
      const TINT_START = 0.15;
      const TINT_END = 0.7;
      const tint = Math.max(0, Math.min(1, (e - TINT_START) / (TINT_END - TINT_START)));
      material.uniforms.u_persistentTint.value = tint;

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    };

    const start = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(tick);
    };
    const stop = () => {
      if (!rafId) return;
      cancelAnimationFrame(rafId);
      rafId = 0;
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) start();
        else stop();
      },
      { rootMargin: '200px 0px' },
    );
    io.observe(container);

    return () => {
      io.disconnect();
      stop();
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      landTex.dispose();
      ukTex.dispose();
      if (renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
    };
  }, [size]);

  const boxW = size + 2 * padX;
  const boxH = size + 2 * padY;
  return (
    <div style={{ width: boxW, height: boxH, position: 'relative' }}>
      <div ref={containerRef} style={{ width: boxW, height: boxH }} />
    </div>
  );
}
